import { useDisclosure } from '@chakra-ui/react';
import { InvoiceStatusEnum } from '@monetize/types/app';
import { getErrorMessage } from '@monetize/utils/core';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  useGetInvoiceByShareId,
  useSetupIntentForInvoiceShare,
} from '../../api/shareService';
import AppLoading from '../../components/App/AppLoading';
import InvoiceDisplay from '../../components/Invoices/InvoiceDisplay';
import {
  MBox,
  MButton,
  MFlex,
  MPageContainer,
  MText,
} from '../../components/Monetize';
import { MEntityStatusBadge } from '../../components/Monetize/MEntityStatusBadge';
import { InvoiceStatusEnumDisplay } from '../../constants/invoice';
import { PAPER_PAGE_MAX_WIDTH } from '../../constants/quotes';
import { ExternalShareErrorPage } from '../../layouts/components/ExternalShareErrorPage';
import { useFlags } from '../../services/launchDarkly';
import Sentry from '../../services/sentry';
import { SharedInvoicePaymentModal } from './components/SharedInvoicePaymentModal';
import { ViewInvoiceActions } from './components/ViewInvoiceActions';

export const InvoiceShareDetails = () => {
  const { tenantId = '', invoiceSecretId = '' } = useParams();
  const { anonymousInvoiceAllowPaymentAmount } = useFlags();
  const paymentModalState = useDisclosure();
  const [publicKey, setPublicKey] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [paymentMethodError, setPaymentMethodError] = useState<boolean>(false);
  const [displaySticky, setDisplaySticky] = useState<boolean>(false);

  const { mutateAsync: generateSetupIntent } = useSetupIntentForInvoiceShare();

  const {
    isLoading,
    data: shareInvoiceData,
    error: shareInvoiceError,
  } = useGetInvoiceByShareId(invoiceSecretId, tenantId);
  const invoice = shareInvoiceData?.invoice;
  const billGroup = shareInvoiceData?.billGroup;
  const unpaidInvoices = shareInvoiceData?.unpaidInvoices;

  useEffect(() => {
    const run = async () => {
      try {
        const apiResp = await generateSetupIntent({
          invoiceSecretId,
          tenantId,
        });
        setClientSecret(apiResp.clientSecret);
        setPublicKey(apiResp.publicKey);
      } catch (ex) {
        Sentry.logger.warn('InvoiceShareDetails - setup intent failed', {
          invoiceSecretId,
          tenantId,
          error: getErrorMessage(ex),
        });
        setPaymentMethodError(true);
      }
    };
    const isVoid = invoice?.status === InvoiceStatusEnum.VOID;
    if (invoiceSecretId && invoice?.status && !isVoid) {
      run();
    }
  }, [invoiceSecretId, tenantId, generateSetupIntent, invoice?.status]);

  const handlePaymentModalClose = () => paymentModalState.onClose();

  const canPayInvoice = useMemo(() => {
    try {
      const isUnpaid =
        invoice?.status === InvoiceStatusEnum.UNPAID &&
        !invoice?.paid &&
        invoice.amount > 0;

      const allowAnonymousPayment =
        isUnpaid &&
        (anonymousInvoiceAllowPaymentAmount === -1 ||
          invoice.amount < anonymousInvoiceAllowPaymentAmount);

      return isUnpaid && allowAnonymousPayment && !paymentMethodError;
    } catch (ex) {
      console.error(ex);
      return false;
    }
  }, [
    anonymousInvoiceAllowPaymentAmount,
    invoice?.amount,
    invoice?.paid,
    invoice?.status,
    paymentMethodError,
  ]);

  useEffect(() => {
    const handleStickyMenu = () => {
      window.scrollY > 50 ? setDisplaySticky(true) : setDisplaySticky(false);
    };

    window.addEventListener('scroll', handleStickyMenu);

    return () => {
      window.removeEventListener('scroll', handleStickyMenu);
    };
  }, []);

  useEffect(() => {
    if (invoice) {
      Sentry.logger.info('InvoiceShareDetails accessed', {
        invoiceSecretId,
        tenantId,
        invoiceStatus: invoice.status,
        canPay: String(canPayInvoice),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoice?.id]);

  useEffect(() => {
    if (paymentModalState.open && invoice) {
      Sentry.logger.info('InvoiceShareDetails - payment modal opened', {
        invoiceSecretId,
        tenantId,
        invoiceStatus: invoice.status,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentModalState.open]);

  useEffect(() => {
    if (shareInvoiceError) {
      Sentry.logger.error('InvoiceShareDetails - data load failed', {
        invoiceSecretId,
        tenantId,
        error: getErrorMessage(shareInvoiceError),
      });
      Sentry.captureException(
        {
          message: 'Error fetching invoice share data',
          extra: {
            invoiceSecretId,
            tenantId,
            shareInvoiceError,
          },
        },
        {
          tags: {
            type: 'API_CALL_ERROR',
          },
        },
      );
    }
  }, [shareInvoiceError]);

  if (isLoading) {
    return <AppLoading />;
  }

  if (shareInvoiceError) {
    return (
      <ExternalShareErrorPage
        title="Error"
        description="There was a problem processing your request, contact your sales representative for further assistance."
      />
    );
  }

  if (invoice?.status === InvoiceStatusEnum.VOID) {
    const replyToEmail =
      shareInvoiceData?.invoiceSettings?.invoiceEmailingConfig?.replyTo ||
      'noreply@monetizenow.io';
    return (
      <ExternalShareErrorPage
        title="Invoice Unavailable"
        description={`This invoice is not eligible to be viewed at this time. Contact "${replyToEmail}" with any questions.`}
      />
    );
  }

  return (
    <MPageContainer height="auto" p="6">
      <MBox
        width="100%"
        display="flex"
        flexDir="column"
        alignItems="center"
        pb="10"
      >
        <MFlex
          alignContent="center"
          alignItems="center"
          justifyContent="space-between"
          maxW={PAPER_PAGE_MAX_WIDTH}
          w="100%"
          pb={[4, 0, 7]}
          pt={[4, 0]}
        >
          {invoice?.status && (
            <MFlex alignItems="center">
              <MText
                fontWeight="bold"
                display={['none', 'none', 'inline']}
                mr=".5rem"
              >
                Status:
              </MText>
              <MEntityStatusBadge
                ml="0"
                status={
                  invoice?.status
                    ? InvoiceStatusEnumDisplay[invoice?.status]
                    : ''
                }
              />
            </MFlex>
          )}

          <MFlex alignItems="center">
            {canPayInvoice && (
              <MButton
                data-testid="pay-invoice-button"
                variant="primary"
                onClick={paymentModalState.onOpen}
                mr="2"
                display={['none', 'none', 'block']}
              >
                Pay Invoice
              </MButton>
            )}
            {!!invoice && (
              <ViewInvoiceActions
                invoice={invoice}
                invoiceSecretId={invoiceSecretId}
                tenantId={tenantId}
              />
            )}
          </MFlex>
        </MFlex>
        {canPayInvoice && (
          <MFlex
            w="100%"
            alignItems="center"
            justifyContent="center"
            position={displaySticky ? 'fixed' : 'relative'}
            bottom={displaySticky ? '0' : 'auto'}
            zIndex={1}
            bg="#FFF"
            boxShadow={displaySticky ? '0px -3px 14px 0px #18243C14' : 'none'}
            p="4"
          >
            <MButton
              data-testid="pay-invoice-button"
              variant="primary"
              onClick={paymentModalState.onOpen}
              display={['block', 'block', 'none']}
              size="lg"
              w="240px"
              minW="0"
            >
              Pay Invoice
            </MButton>
          </MFlex>
        )}
        <InvoiceDisplay
          isInvoiceSharePage
          loadingInvoice={isLoading}
          invoice={invoice}
          unpaidInvoices={unpaidInvoices}
          invoiceSettings={shareInvoiceData?.invoiceSettings}
          canPayInvoice={canPayInvoice}
          onPayInvoice={paymentModalState.onOpen}
          companyLogoData={{
            isLoading,
            data: shareInvoiceData?.logo
              ? `data:image/png;base64,${shareInvoiceData?.logo}`
              : '',
          }}
          tenantId={tenantId}
          invoiceSecretId={invoiceSecretId}
        />

        {/* {invoice && <InvoiceCreditNotes invoice={invoice} />} */}
      </MBox>

      {paymentModalState.open && invoice && canPayInvoice && (
        <SharedInvoicePaymentModal
          allowPartialPayment={
            !!shareInvoiceData?.billGroupSettings?.allowPartialPayment
          }
          clientSecret={clientSecret}
          invoice={invoice}
          paymentMethodId={billGroup?.paymentMethodId}
          invoiceSecretId={invoiceSecretId}
          isOpen={paymentModalState.open}
          publicKey={publicKey}
          tenantId={tenantId}
          paymentConfiguration={shareInvoiceData?.paymentConfiguration}
          onClose={handlePaymentModalClose}
        />
      )}
    </MPageContainer>
  );
};
